import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link, useSearchParams } from "react-router-dom";
import {
  MapPin,
  Truck,
  ArrowRight,
  BadgePoundSterling,
  Box,
} from "lucide-react";
import { useFetchActiveJobs } from "@/hooks/useJobQueries";
import SignOutRedirect from "@/components/auth/SignOutRedirect";
import { useAnonymousVisitor } from "@/hooks/useAnonymousVisitor";
import { useEffect } from "react";

const LandingPage = () => {
  const { createVisitor } = useAnonymousVisitor();
  const { data: jobs, isLoading, error } = useFetchActiveJobs();
  const [searchParams] = useSearchParams();

  const getIconForJobType = (type: string) => {
    switch (type) {
      case "Driver":
        return <Truck className="h-4 w-4 mr-1" />;
      case "Warehouse":
        return <Box className="h-4 w-4 mr-1" />;
      default:
        return <Truck className="h-4 w-4 mr-1" />;
    }
  };

  useEffect(() => {
    // Get source parameter from URL if it exists
    const source = searchParams.get("source");

    // Create visitor with source information if available
    createVisitor(source ? { source } : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Logo Section */}
        <div className="flex justify-center mb-8">
          <img
            src="/images/Hived-logo.svg"
            alt="Hived Logo"
            className="w-20 h-20"
          />
        </div>

        {/* Hero Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-jungle mb-4">
            Current Open Positions
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Join our growing team with delivery and warehouse opportunities
            across the UK
          </p>
        </div>

        {/* Job Postings */}
        <div className="flex flex-col space-y-6 max-w-2xl mx-auto">
          {isLoading && (
            <div className="text-center py-10">
              <p className="text-gray-600">Loading job postings...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-10">
              <p className="text-red-500">{error.message}</p>
              <Button
                className="mt-4 bg-honeycomb hover:bg-amber-500 text-white"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </div>
          )}
          {!isLoading && !error && jobs.length === 0 && (
            <div className="text-center py-10">
              <p className="text-gray-600">
                No job postings available at this time.
              </p>
            </div>
          )}

          {!isLoading &&
            !error &&
            jobs.map((job) => (
              <Card
                key={job.id}
                className="p-6 hover:shadow-lg transition-shadow"
              >
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-jungle mb-2">
                    {job.title}
                  </h3>
                  <div className="flex items-center text-gray-600 mb-2">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span className="text-sm">{job.location}</span>
                  </div>
                  <div className="flex items-center text-gray-600 mb-2">
                    {getIconForJobType(job.type)}
                    <span className="text-sm">{job.type}</span>
                  </div>
                  {job.salary && (
                    <div className="flex items-center text-honeycomb font-semibold">
                      <BadgePoundSterling className="h-4 w-4 mr-1" />
                      <span className="text-sm">{job.salary}</span>
                    </div>
                  )}
                </div>

                <p className="text-gray-600 mb-4 text-sm">{job.description}</p>

                <SignOutRedirect job={job} />
              </Card>
            ))}
        </div>

        {/* Login Section - Matching basic details page style */}
        <div className="mt-8 text-center py-[24px] my-[2px]">
          <p className="text-sm text-gray-500 mb-2">
            Already started an application?
          </p>
          <Link to="/login">
            <Button variant="outline" className="w-full md:w-auto py-0 my-0">
              Return to your application <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        {/* HQ Jobs Section */}
        <div className="flex justify-center items-center text-center py-[24px] my-[2px] border-t border-gray-200">
          <p className="text-sm text-gray-500 mr-2">Looking for HQ jobs?</p>
          <a
            href="https://hivedhq.teamtailor.com/jobs"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center text-sm"
          >
            View HQ opportunities.
          </a>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
